import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { ArrowUpRight, Lock, ShieldCheck, ScanEye, Wallet, TrendingUp } from "lucide-react";

// Iter 167 — Hero art swapped for the branded trophy scene (VERIFIED
// GAMBLERS shield on a poker table). The asset is 1600×2000 (4:5), an
// exact match for the existing `aspect-[4/5]` container so no layout
// change is needed. Because the image has its own baked-in typography
// ("YOUR EDGE STARTS HERE" + "Real Gamblers — Real Wagers — Real Edge"),
// the previous "€ 128,540 · Under-code volume" overlay tile was removed
// (would clash with the on-image copy) and the `opacity-60` + dark
// gradient were dropped so the artwork reads cleanly.
const HERO_IMG = "/hero-2026.webp?v=167";

// Iter 184/185 — Homepage trust-stat tile with a scroll-into-view count-up.
// The counter runs a single ~1.2s ease-out ramp from 0 to `value` the
// first time BOTH conditions are met: value is > 0 (data has loaded)
// AND the tile intersects the viewport. Guarding on value>0 fixes the
// Iter-184 bug where the observer fired on the initial 0 render (tile is
// above the fold), locked `started`, and then ignored the real value
// when the /public/stats fetch resolved. No external animation dep —
// plain rAF + IntersectionObserver keeps this zero-cost.
function CountUpTile({ value, label, testId }) {
  const [n, setN] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    if (!ref.current) return undefined;
    if (started.current) return undefined; // already animated for this mount
    const target = Math.max(0, Math.floor(Number(value) || 0));
    if (target === 0) { setN(0); return undefined; }  // wait for real data
    const animate = () => {
      started.current = true;
      const dur = 1200;
      const t0 = performance.now();
      const step = (t) => {
        const p = Math.min(1, (t - t0) / dur);
        // easeOutCubic — quick early ramp, gentle landing so the number
        // "sticks" without overshoot.
        const eased = 1 - Math.pow(1 - p, 3);
        setN(Math.round(target * eased));
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) if (e.isIntersecting) { animate(); io.disconnect(); break; }
    }, { threshold: 0.25 });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [value]);
  return (
    <div
      ref={ref}
      data-testid={testId}
      className="border border-[#2DD4A7]/30 bg-[#2DD4A7]/[0.06] p-6 lg:p-8"
      style={{ borderRadius: "16px", boxShadow: "0 12px 32px rgba(45, 212, 167, 0.12), inset 0 1px 0 rgba(45, 212, 167, 0.10)" }}
    >
      <div
        className="font-display font-black tracking-tighter tabular-nums text-[#E8ECF4]"
        style={{ fontFamily: "'Space Grotesk', Inter, sans-serif", fontSize: "36px", lineHeight: 1 }}
        data-testid={`${testId}-value`}
      >{n.toLocaleString()}</div>
      <div className="mt-3 font-mono uppercase tracking-widest text-[#8B97AE]" style={{ fontSize: "11px", fontWeight: 600, letterSpacing: "0.14em" }} data-testid={`${testId}-label`}>{label}</div>
    </div>
  );
}

export default function Landing() {
  const { t } = useI18n();
  // Iter 184/185 — Baselines from `/api/public/stats` (60s server-cached), so
  // an anonymous landing hit is a single non-authed request. On failure
  // we leave the tiles at 0 rather than fabricating numbers.
  const [stats, setStats] = useState({ verified_players: 0, verified_streamers: 0, contracts_active: 0 });
  // Iter 186 — Public launch gate. The trust-stat block is hidden entirely
  // (not rendered — no DOM node, no fetch triggered) until Thursday
  // 2026-09-17 00:00 UTC. On/after that instant the block appears
  // automatically; no admin toggle, no redeploy. Using `Date.UTC(y, m-1, d)`
  // pins the compare to UTC regardless of the visitor's timezone.
  const TRUST_STATS_LAUNCH_UTC_MS = Date.UTC(2026, 8, 17, 0, 0, 0);
  const trustStatsLaunched = Date.now() >= TRUST_STATS_LAUNCH_UTC_MS;
  useEffect(() => {
    if (!trustStatsLaunched) return undefined;
    let alive = true;
    api.get("/public/stats").then((r) => { if (alive) setStats(r.data || {}); }).catch(() => {});
    return () => { alive = false; };
  }, [trustStatsLaunched]);
  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden grain">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-24 lg:pt-24 lg:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-7">
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black leading-[0.95] tracking-tighter mb-6">
              {t("landing.hero.title")}
            </h1>
            <p className="text-base sm:text-lg text-[#8B97AE] leading-relaxed max-w-2xl mb-10">{t("landing.hero.sub")}</p>
            <div className="flex flex-wrap gap-3">
              <Button asChild className="rounded-none bg-[#2DD4A7] text-[#04150F] hover:bg-[#F5B841] hover:text-[#04150F] h-12 px-6 font-semibold text-sm transition-colors">
                <Link to="/register/player" data-testid="hero-cta-player">{t("landing.cta.player")} <ArrowUpRight className="ml-1.5 w-4 h-4" /></Link>
              </Button>
              <Button asChild variant="ghost" className="rounded-none border border-white/20 h-12 px-6 font-semibold text-sm text-white hover:bg-white/10 transition-colors">
                <Link to="/register/streamer" data-testid="hero-cta-streamer">{t("landing.cta.streamer")} <ArrowUpRight className="ml-1.5 w-4 h-4" /></Link>
              </Button>
            </div>
            {/* Iter 184/185 — Trust-stat tiles directly under the CTAs.
                Iter 186 — gated behind the public launch timestamp: not
                rendered at all before 2026-09-17 00:00 UTC. */}
            {trustStatsLaunched && (
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="landing-trust-stats">
              <CountUpTile testId="stat-verified-players" value={stats.verified_players} label={t("landing.stats.verifiedPlayers")} />
              <CountUpTile testId="stat-verified-streamers" value={stats.verified_streamers} label={t("landing.stats.verifiedStreamers")} />
              <CountUpTile testId="stat-contracts-active" value={stats.contracts_active} label={t("landing.stats.contractsActive")} />
            </div>
            )}
          </div>
          <div className="lg:col-span-5 relative">
            <div className="aspect-[4/5] border border-white/15 overflow-hidden relative bg-[#151B28]">
              <img
                src={HERO_IMG}
                alt="Verified Gamblers — your edge starts here"
                className="w-full h-full object-cover"
                loading="eager"
                data-testid="landing-hero-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Iter 215 — Lightweight X/Twitter follow banner. Sits between the
          hero stat tiles and the "Built on rules we don't bend" section.
          Single centred row; no heading, so it doesn't compete with the
          section titles above/below. */}
      <section className="border-t border-white/10" data-testid="landing-x-follow">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 flex flex-col sm:flex-row items-center justify-center gap-4 text-center">
          <p className="text-sm text-[#8B97AE]">{t("landing.xFollow.copy")}</p>
          <Button
            asChild
            className="rounded-none bg-[#2DD4A7] text-[#04150F] hover:bg-[#F5B841] hover:text-[#04150F] h-10 px-5 font-semibold text-xs uppercase tracking-widest transition-colors"
          >
            <a
              href="https://x.com/VeriGamblers"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="landing-x-follow-cta"
            >
              {t("landing.xFollow.cta")} <ArrowUpRight className="ml-1.5 w-4 h-4" />
            </a>
          </Button>
        </div>
      </section>

      {/* Four rules */}
      <section className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-14">
            <div className="lg:col-span-5">
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight">{t("landing.rules.title")}</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
            {[
              { i: ScanEye, k: "landing.rule.1" },
              { i: ShieldCheck, k: "landing.rule.2" },
              { i: Lock, k: "landing.rule.3" },
              { i: Wallet, k: "landing.rule.4" },
            ].map((r, idx) => (
              <div key={idx} className="bg-[#0B0E14] p-8 lg:p-10 flex gap-5" data-testid={`rule-${idx + 1}`}>
                <div className="w-10 h-10 shrink-0 border border-white/20 flex items-center justify-center">
                  <r.i className="w-5 h-5" strokeWidth={2} />
                </div>
                <div>
                  <div className="font-mono text-xs text-[#2DD4A7] mb-2">0{idx + 1}</div>
                  <p className="text-base leading-relaxed">{t(r.k)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-t border-white/10 bg-[#151B28]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight mb-14">{t("landing.how.title")}</h2>
          {/* Iter 136 — 8 steps in a 3 + 3 + 2 layout on desktop.
              Steps 07 & 08 centre on the last row via `lg:col-start-2`.
              Below `lg` the grid collapses to 2-col (md) or 1-col (mobile). */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6">
            {["step1", "step2", "step3", "step4", "step5", "step6", "step7", "step8"].map((s, idx) => {
              const startCol = idx === 6 ? "lg:col-start-2" : "";
              return (
                <div key={s} className={`border border-white/15 p-8 relative lg:col-span-2 ${startCol}`} data-testid={`how-${s}`}>
                  <div className="font-display text-6xl font-black text-[#2DD4A7]/80 mb-4">{String(idx + 1).padStart(2, "0")}</div>
                  <h3 className="font-display text-xl font-bold mb-3">{t(`landing.how.${s}.title`)}</h3>
                  <p className="text-sm text-[#8B97AE] leading-relaxed">{t(`landing.how.${s}.body`)}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing lock */}
      <section className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight">{t("landing.pricing.title")}</h2>
            </div>
            <div className="lg:col-span-7">
              <div className="border border-white/15 p-8 lg:p-10 relative overflow-hidden" data-testid="pricing-locked-card">
                <Lock className="w-8 h-8 text-[#F5B841] mb-6" strokeWidth={2} />
                <p className="text-base text-[#8B97AE] leading-relaxed mb-6">{t("landing.pricing.hidden")}</p>
                <div className="flex flex-wrap gap-3">
                  <Button asChild className="rounded-none bg-[#2DD4A7] text-[#04150F] hover:bg-[#F5B841] hover:text-[#04150F] h-11 px-5 font-semibold transition-colors">
                    <Link to="/register/streamer" data-testid="pricing-cta-streamer">{t("landing.cta.streamer")} <TrendingUp className="ml-1.5 w-4 h-4" /></Link>
                  </Button>
                </div>
                <div className="mt-8 grid grid-cols-5 gap-2 font-mono text-[10px] uppercase tracking-widest text-[#8B97AE]">
                  {["0", "50k", "100k", "250k", "500k"].map((v) => (
                    <div key={v} className="border-t border-white/20 pt-2">{v}</div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
